import _debug from 'debug'

import { BLOB_BLOCK_SIZE, VideoCache } from './VideoCache'
import { VideoCacheRecord, VideoBlob, VideoDownloadResult, INHERITED_UPLOAD_ACCESS_DATE } from './VideoCacheRecord'
import { IProgress } from './API2'
import { sortBy, max, countBy, once } from 'underscore'
import { observable } from 'mobx'
import { getHasElectronContext, isSlttAppStorageEnabled } from './SlttAppStorage'
import { doOnceWhenBackOnline, getIsAppOnlineOrWait } from '../components/utils/ServiceStatus'
import { _LevelupDB } from './_LevelupDB'
import { displayError, sendBeacon } from '../components/utils/Errors'
import { t } from 'ttag'
import { HOTFIX_UNBLOCK_CACHE_CLEAN_RECENT_BLOBS } from './VideoCacheHotFix'
import { didHappenedWithinHourWindow, getDbSaveErrorDetectedAt, getStorageUsageAtCrash, HOURS_TO_DISABLE_IMPLICIT_DOWNLOADS, toMb, HOURS_TO_KEEP_PORTION_DOWNLOADS, getInPortionDownloadMode } from './VideoCache.utils'

let log = _debug('sltt:VideoCacheDownloader')
let dbg = _debug('slttdbg:_VideoCacheDownloader')

const warnOnceVcrSizeBlockSizeMismatch = once((msg: string) => {
    console.warn(msg)
})

const warnOnceEstimatorUndefined = once((msg: string, { quota, usage }: { quota?: number, usage?: number }) => {
    console.warn(msg, { quota, usage })
})

const fetchLimit = 4 // max simultaneous fetches
const perUrlFetchLimit = 3 // max simultaneous fetches to different chunks of same url
const explicitRequestPriorityBump = 1000000

type Estimate = { quota: number, usage: number }
type SafeToDeleteVcrsResult = {
    bytesToFree: number,
    estimate: Estimate,
    vcrsSafeToDelete: VideoCacheRecord[],
    vcrsUnsafeToDelete: VideoCacheRecord[],
    vcrsUnsafeToDeleteWithContent: VideoCacheRecord[],
    vcrsSafeToDeleteBlobsSize: number,
}

export const extractLastTimestamp = (_id: string) => {
    if (_id.includes('/project-images')) {
        // Example: `TESTnm/project-images/full/2021_11_15T17_07_10_558Z-1`
        const creationDateRaw = _id.split('/').slice(-1)[0].split('-')[0] // 2021_11_15T17_07_10_558Z
        const [dateRaw, timeRaw] = creationDateRaw.split('T')
        const dateParts = dateRaw.split('_')
        const [hh, mm, ss, zzz] = timeRaw.split('_')
        const date = new Date(`${dateParts[0]}-${dateParts[1]}-${dateParts[2]}T${hh}:${mm}:${ss}.${zzz}`)
        const idFormat = _LevelupDB.dateToId(date)
        return `${idFormat}/${creationDateRaw}`
    }
    if (_id.includes('/pasDoc_')) {
        // handle passage doc format: 111111_111111/222222_222222/pasDoc_221231_163557/2024_08_31T11_34_55.102Z.txt-1
        const [pasDocPart, timestamp2] = _id.split('.txt-')[0].split('/').slice(-2)
        const timestamp1 = pasDocPart.split('pasDoc_')[1] // 221231_163557
        return `${timestamp1}/${timestamp2}` // 221231_163557/2024_08_31T11_34_55.102Z
    }
    const lastTimestamp = _id.split('-')[0].split('/').slice(-1)[0].slice(-13) // 241201_123456
    return lastTimestamp
}

/**
 * Goal: Download the highest priority videos first.
 * 
 * Constraints:
 *   - No more than fetchLimit (4) simultaneous downloads from S3
 *   - If this block is has been marked 'notUploaded' and there is an active fetch for
 *     a lower numbered block, this black cannot be started
 */

export interface IVideoDownloadQuery {
    message: string,
    blob: Blob | undefined,
}

// Request to download a chunk of a video blob
export class ScheduledDownload {
    public progress: IProgress | null = null // { total: number, loaded: number }
        // when non-null there is a fetch in progress for this chunk

    public delayUntil = 0
        // When non-zero, don't allow this fetch to be started until this date

    public retries = 0

    constructor(
        public videoUrl: string,
        public chunkNum: number,   // 0 origin chunk number for this blob
        public priority: number,   // bigger is more urgent
        public vcr: VideoCacheRecord,
    ) { }

    match(vcr: VideoCacheRecord, chunkNum: number) {
        return this.videoUrl === vcr._id && this.chunkNum === chunkNum
    }

    exponentialBackoffRetry(result: VideoDownloadResult) {
        let backoff = 3600
        if (result !== VideoDownloadResult.FAILED) {
            let backoffs = [2, 2, 2, 2, 2, 5, 5, 5, 5, 5, 5, 20, 20, 20, 20, 20, 60, 60, 60, 60, 60, 10*60]
            backoff = backoffs[Math.min(this.retries, backoffs.length - 1)]
        }

        this.delayUntil = Date.now() + 1000*backoff
        log(`delayUntil -> now: backoff ${backoff}\n${this.videoUrl}[${this.chunkNum}]\n${new Date(this.delayUntil).toISOString()}\n${new Date(Date.now()).toISOString()}`)
        
        ++this.retries
    }

    clone() {
        let c = new ScheduledDownload(this.videoUrl, this.chunkNum, this.priority, this.vcr)
        if (this.progress) {
            let {total, loaded} = this.progress
            c.progress = {total, loaded}
        }
        
        c.delayUntil = this.delayUntil
        c.retries = this.retries

        return c
    }

    static eq(s1: ScheduledDownload, s2: ScheduledDownload) {
        return s1.videoUrl === s2.videoUrl && s1.chunkNum === s2.chunkNum
    }
}

export class VideoCacheDownloader {
    static unittestQuota = 0
    static unittestUsage = 0
    /**
     * @type {boolean} true if we are in batch download mode
     * When true, makeSpaceAvailable() will not delete any blobs
     *           
     * To prevent makeSpaceAvailable() from cleaning up videos that
     * the user actually wants to access while offline,
     * set batchDownloadMode to `true`, 
     * so that updatePortionDownloadedAt() will record when the last of 
     * those portion videos were being downloaded.
     * NOTE: after the videos have been downloaded for an hour
     * getInPortionDownloadMode() can be used to check if we want
     * to try to keep videos from the past hour in the cache because
     * the next non-batch download will trigger makeSpaceAvailable()
     * and it otherwise will try to remove up to 1Gb before each download.
     * So it's best to instruct the user to disconnect
     * soon after downloading these from the network
     * to ensure those videos are still accessible offline
     */
    static batchDownloadMode = false
    static outOfSpaceReportedToRollbar = false
    static exceededQuotaReportedToRollbar = false

    static systemError = (err: any) => {
        console.error(err)
    }

    requestCount = 0   
       // track request number so that latest requests
       // can be given higher priority

    scheduledDownloads: ScheduledDownload[] = []
    @observable downloadCount = 0

    // Maps blob ID to timestamps (ms) of successful S3 fetches in the past 24 hours.
    // Used to detect blobs that are downloaded more than once.
    recentDownloadTimestamps = new Map<string, number[]>()

    // Time that most recent duplicate download detected. 0 if none detected.
    duplicateDownloadTimestamp = 0

    // last fixed size of VideoCacheRecord, if any, that was detected and fixed
    lastFixedSizeOfVideoCacheRecordTimestamp = 0

    // Snapshots are used for rollbar reporting
    // Raw result of navigator.storage.estimate(): quota is the browser-granted storage limit,
    // usage is total bytes used across all storage (IndexedDB, Cache API, etc.) for this origin.
    // quota/usage are set to -1 if navigator.storage.estimate() is unavailable (e.g. iOS/Safari).
    snapshotNavigatorStorageEstimate: Estimate | null = null

    // Adjusted quota/usage used for space management decisions 
    // (set alongside snapshotNavigatorStorageEstimate).
    // Quota is derived from the raw browser quota but capped at 
    // min(80% of browser quota, videoCacheLimitGB hard cap),
    // and further lowered to match usage-at-crash when a recent DB save error is detected.
    // usage mirrors the raw navigator.storage.estimate() usage (or 250MB assumed on iOS).
    snapshotFinalEstimate: Estimate | null = null

    snapshotNavigatorStoragePersisted: boolean | null = null
    snapshotSafeToDeleteVcrs: { bytesToFree: number, vcrsSafeToDeleteBlobsSize: number, vcrsUnsafeToDeleteSize: number, vcrsUnsafeToDeleteWithContent: VideoCacheRecord[]  } | null = null
    snapshotAfterMsg: string | null = null

    constructor(public displayError?: (message: any) => void) {
        if (displayError) VideoCacheDownloader.systemError = displayError

        setInterval(this.startDownloads.bind(this), 2000)
    }

    toJSON() {
        const { scheduledDownloads, ...rest } = this
        return {
            ...rest,
            batchDownloadMode: VideoCacheDownloader.batchDownloadMode,
            outOfSpaceReportedToRollbar: VideoCacheDownloader.outOfSpaceReportedToRollbar,
            exceededQuotaReportedToRollbar: VideoCacheDownloader.exceededQuotaReportedToRollbar,
            unittestQuota: VideoCacheDownloader.unittestQuota,
            scheduledDownloadsTop2: scheduledDownloads.slice(0, 2), // limit to 2 for debugging
        }
    }

    // Return a clone of the scheduled downloads queue.
    // Sort items in progress to the top.
    // This is used for debugging.
    queryScheduledDownloads() {
        let sds: ScheduledDownload[] = []

        this.scheduledDownloads.forEach(sd => {
            let index = sds.findIndex(_sd => _sd.videoUrl === sd.videoUrl)
            if (index < 0) {
                sds.push(sd)
            } else if (sd.priority > sds[index].priority) {
                sds[index] = sd
            }
        })

        sds = sortBy(sds, sd => -sd!.priority)
        return sds
    }

    async getProgress(videoUrl: string) {
        let vcr = await VideoCacheRecord.get(videoUrl)
        let { numberUploaded, numberDownloaded, totalBlobs } = vcr
        return { numberUploaded, numberDownloaded, totalBlobs }
    }

    // This method gets called every few seconds from a control that needs the
    // specified video data. If the download is complete the blob is eturned.
    // If no download has been scheduled yet for the url it is scheduled.
    // Otherwise a progress message for the download is returned.
    async queryVideoDownload(videoUrl: string): Promise<IVideoDownloadQuery> {
        let vcr = await VideoCacheRecord.get(videoUrl)

        // If data is already present because the video was locally created, return video blob
        if (vcr.uploadeds.length) {
            dbg(`queryVideoDownload locally created`)
            let blob = await vcr.getVideoBlob()
            if (blob) {
                return { message: '', blob }
            }
        }

        // Ensure status array for downloads of individual chunks of video initialized
        await vcr.setupDownload()
        
        // If already compltedly downloaded, return video blob
        if (vcr.downloaded) {
            dbg(`queryVideoDownload downloaded`)
            let blob = await vcr.getVideoBlob()
            if (blob) {
                return { message: '', blob }
            }
            // If we did not get the blob, it means that we incorrect in
            // thinking it was already downloaded, fall through to download
        }

        if (!isSlttAppStorageEnabled() && !(await getIsAppOnlineOrWait())) {
            return { message: "No internet connection!", blob: undefined }
        }

        this.scheduleDownload(vcr, true)

        return { message: vcr.downloadMessage, blob: undefined }
    }

    /**
     * (optionally) schedule a video to be downloaded.
     * Called in two situations:
     *    1) SLTT is starting up and sees an object that was created in the last 7 days.
     *    2) The user requrested that all the downloadable objects for a specific portions
     *       should be downloaded so that they are available offline.
     * @returns true iff specified video already present in video cache
     */
    async implicitVideoDownload(videoUrl: string) {
        log(`implicitVideoDownload ${videoUrl}`)

        let vcr = await VideoCacheRecord.get(videoUrl)

        // If data is already present because the video was locally created, return true
        if (vcr.uploadeds.length) return true

        // Ensure status array for downloads of individual chunks of video initialized
        await vcr.setupDownload()

        // If already compltedly downloaded, done
        if (vcr.downloaded) return true

        this.scheduleDownload(vcr, false /* tag as implicit download request */)

        return false
    }

    // Add any entries to scheduledDownloads.
    // Each entry represents a chunk that needs to be downloaded
    scheduleDownload(vcr: VideoCacheRecord, explicitRequest: boolean) {

        // process chunks backwards because we want to start downloading
        // with first chunk and the last chunk processed has the hightest
        // priority
        for (let i = vcr.downloadeds.length-1; i >= 0; --i) {
            if (!vcr.downloadeds[i]) {
                // chunk has not downloaded yet
                let j = this.scheduledDownloads.findIndex(sd => sd.match(vcr, i))
                if (j >= 0) {
                    // Download already scheduled.
                    // Nothing to do unless this video was not explicitly requested before
                    // and now it is. If so, bump priority]
                    let sd = this.scheduledDownloads[j]
                    if (explicitRequest && sd.priority < explicitRequestPriorityBump) {
                        sd.priority += explicitRequestPriorityBump
                    }
                } else {
                    // Download not scheduled yet, schedule it.
                    // The recently requested videos have the higher download priority
                    // If a video has been explictly requested for display by a user
                    // it gets a big priority bump.
                    this.requestCount += 1
                    let priority =this.requestCount
                    if (explicitRequest) { priority += explicitRequestPriorityBump }
                    log('add ScheduledDownload', vcr.videoBlobId(i), priority)

                    let sd = new ScheduledDownload(vcr._id, i, priority, vcr)
                    this.scheduledDownloads.push(sd)
                    this.downloadCount = this.scheduledDownloads.length
                }
            }
        }

        this.startDownloads()
    }

    clearImplicitDownloads() {
        this.scheduledDownloads = this.scheduledDownloads.filter(
            sd => sd.priority >= explicitRequestPriorityBump || sd.progress !== null
        )
        this.downloadCount = this.scheduledDownloads.length
    }

    startDownloads() {
        let sds = this.scheduledDownloads
        if (!sds.length) return

        let now = Date.now()
        let urlCount = countBy(sds.filter(sd => sd.progress), sd => sd.videoUrl)

        let calcPriority = (sd: ScheduledDownload) => {
            let {delayUntil, progress, videoUrl, chunkNum, priority, vcr} = sd
            // log(`delayUntil -> now\n${videoUrl}\n${new Date(delayUntil).toISOString()}\n${new Date(now).toISOString()}`)

            // After errors, we delay a while before retrying
            if (delayUntil && delayUntil > now) return -1
            
            // Should not restart if already in progress
            if (progress) return -2
            
            // Only allow a limited number fetches for each url (so that
            // one video cannot hog all the download bandwidth)
            if ((urlCount[videoUrl] || 0) >= perUrlFetchLimit) return -3

            // If an earlier chunk has not been uploaded or had an exception, don't try to download this chunk
            let previous = vcr.downloadResults.slice(0, chunkNum)
            if (previous.includes(VideoDownloadResult.MISSING)) return -4
            if (previous.includes(VideoDownloadResult.FAILED)) return -5

            return priority
        }

        while (true) {
            let inProgress = this.scheduledDownloads.filter(sd => sd.progress)
            dbg(`inProgress = ${inProgress.length}`)
            
            if (inProgress.length >= fetchLimit) break;

            if (sds.length === 0) break
            let topSd = max(sds, calcPriority) as ScheduledDownload
            
            // Break if best download items have negative priority
            let priority = calcPriority(topSd)
            if (priority < 0) { 
                dbg(`negative priority [${topSd.videoUrl}] ${priority}`)
                break
            }

            urlCount[topSd.videoUrl] = urlCount[topSd.videoUrl] + 1
            topSd.progress = {total: 0, loaded: 0}

            // usually no point in calling startDownload if we're not online,
            // however, if we're connected to local team storage, then proceed
            const alwaysActAsIfOnline = isSlttAppStorageEnabled()
            doOnceWhenBackOnline('startDownload', () => {
                this.startDownload(topSd)
                .catch(error => {
                    // NEVER supposed to happen
                    VideoCacheDownloader.systemError(error)
                })
            }, { doNow: alwaysActAsIfOnline })
        }
    }

    // Start download for a single video chunk.
    // DO NOT throw an exception.
    // On error
    //      - write message to vcr
    //      - remove request from scheduledDownloads
    async startDownload(sd: ScheduledDownload) {
        let _id_ = `${sd.videoUrl}[${sd.chunkNum}]`

        dbg(`startDownload ${_id_}`)

        try {
            let vcr = await VideoCacheRecord.get(sd.videoUrl)

            // Ensure that the video record is touched (which updates accessDate) so that it is not deleted
            await vcr.touch()

            // Clear out space in cache if possible
            if (!await this.makeSpaceAvailable()) {
                await vcr.setDownloadMessage(t`Browser storage may be low`)
                // this.downloadCompleted(sd)
                if (!VideoCacheDownloader.outOfSpaceReportedToRollbar) {
                    doOnceWhenBackOnline('makeSpaceAvailable failed', () =>
                        displayError(
                            'Unable to free up space for download',
                            t`Unable to free up space for download`
                        )
                    )
                    VideoCacheDownloader.outOfSpaceReportedToRollbar = true
                }
                // continue until "QuotaExceededError" error
                // then the user is REALLY stuck.
                // return
            }

            if (vcr.downloadeds[sd.chunkNum]) { 
                this.downloadCompleted(sd)
                return
            }

            let recordProgress = (progress: IProgress) => {
                // Only update progress if sd.progress is not null
                // see https://app.rollbar.com/a/biblesocieties/fix/item/SLTT/1622/occurrence/440601349100#detail
                if (sd.progress) {
                    sd.progress.total = progress.total
                    sd.progress.loaded = progress.loaded

                    this.setProgressMessage(sd.videoUrl)
                        .catch()
                } else {
                    // already completed or encountered an error?
                    console.error(`startDownload: sd.progress is null for ${_id_}, cannot record progress`)
                }
            }

            let result = await vcr.fetchBlob(sd.chunkNum, recordProgress)

            if (result !== VideoDownloadResult.SUCCESS) {
                // fetch failed
                sd.exponentialBackoffRetry(result)
                await vcr.setDownloadMessage(result)
            } else {
                // fetchBlob succeeded
                await this.setProgressMessage(sd.videoUrl)
                this.recordDownloadAndCheckDuplicate(vcr.videoBlobId(sd.chunkNum))
                this.downloadCompleted(sd)
            }

            sd.progress = null
        } 
        catch (err) {
            sd.progress = null
            sd.exponentialBackoffRetry(VideoDownloadResult.FAILED)
            VideoCacheDownloader.systemError(err)
        }
    }

    // Set progress message by totaling the number of partially downloaded
    // blocks and fully downloaded blocks.

    async setProgressMessage(videoUrl: string) {
        let vcr = await VideoCacheRecord.get(videoUrl)

        let downloadedBlocks = vcr.downloadeds.filter(d => d).length

        let partialBlocks = 0
        for (let sd of this.scheduledDownloads) {
            if (sd.videoUrl === videoUrl && sd.progress && sd.progress.loaded) {

                // If a bug causes a block that has been previously downloaded to
                // start downloading again, avoid double counting the block
                // when calculating progress
                if (vcr.downloadeds[sd.chunkNum]) {
                    downloadedBlocks -= 1
                }

                partialBlocks += sd.progress.loaded / sd.progress.total
            }
        }
        
        let totalBlocks = vcr.seqNum()
        let percent = 100 * (partialBlocks + downloadedBlocks) / totalBlocks

        await vcr.setDownloadMessage(`Downloading ... ${percent.toFixed(1)}%`)
    }

    /** Instrumentation to detect duplicate download bug that is running up AWS expenses */
    private recordDownloadAndCheckDuplicate(blobId: string) {
        const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000
        const timestamps = (this.recentDownloadTimestamps.get(blobId) ?? [])
            .filter(t => t > twentyFourHoursAgo)
        timestamps.push(Date.now())
        this.recentDownloadTimestamps.set(blobId, timestamps)
        if (timestamps.length >= 2) {
            this.onDuplicateDownloadDetected(blobId, timestamps)
        }
    }

    // Called when the same S3 blob is successfully downloaded more than once in 24 hours.
    private onDuplicateDownloadDetected(blobId: string, timestamps: number[]) {
        this.duplicateDownloadTimestamp = Date.now()
        doOnceWhenBackOnline('onDuplicateDownloadDetected', () =>
            sendBeacon('onDuplicateDownloadDetected', {
                blobId,
                timestamps: timestamps.map(ms => new Date(ms).toISOString()),
            })
        )
    }

    downloadCompleted(sd: ScheduledDownload) {
        let _id_ = `${sd.videoUrl}[${sd.chunkNum}]`
        log('downloadCompleted', _id_)

        let before = this.scheduledDownloads.length
        this.scheduledDownloads = this.scheduledDownloads.filter(_sd => !ScheduledDownload.eq(_sd, sd))
        this.downloadCount = this.scheduledDownloads.length

        if (before === this.scheduledDownloads.length) {
            debugger
        }
        
        // this.startDownloads()
    }

    /**
     *  This is always called before starting to download a video.
     *  If we have enough available cache space already, just return.
     *  Otherwise, limit cache size to 90% of available space by deleting old entries.
     *  @return {boolean} True iff we can delete enough items to fall within our space quota
     */
    async makeSpaceAvailable() {
        /** number of tries to free up needed bytes to make our quota. max is typically 10Gb */
        let tries = 0
        const oneGb = 1024 * 1024 * 1024
        /** byte target to free (currently max at oneGb per pass) */
        let requiredToFree = oneGb
        /** estimated bytes deleted, calculated by expected blob sizes */
        let totalEstimatedBytesDeleted = 0
        /** initial bytes to free target */
        let initialBytesToFree = 0

        while (true) {
            const { bytesToFree, vcrsSafeToDelete, estimate, vcrsUnsafeToDeleteWithContent, vcrsSafeToDeleteBlobsSize } = await this.getSafeToDeleteVcrs()
            if (initialBytesToFree === 0) { 
                initialBytesToFree = bytesToFree
            }
            const bytesFreed = initialBytesToFree - bytesToFree
            const stats = [
                `bytesFreed: ${toMb(bytesFreed)}mb`,
                `totalEstimatedBytesDeleted: ${toMb(totalEstimatedBytesDeleted)}mb`,
                `requiredToFree: ${toMb(requiredToFree)}mb`,
                `initialBytesToFree: ${toMb(initialBytesToFree)}mb`,
                `last bytesToFree: ${toMb(bytesToFree)}mb`,
                `vcrsSafeToDeleteBlobsSize: ${toMb(vcrsSafeToDeleteBlobsSize)}mb`,
                `vcrsSafeToDelete: ${vcrsSafeToDelete.length}`,
                `vcrsUnsafeToDeleteWithContent: ${vcrsUnsafeToDeleteWithContent.length}`,
                `usage: ${toMb(estimate.usage)}mb`,
                `quota: ${toMb(estimate.quota)}mb`
            ].join('\n\t')
            const afterMsg = `after ${tries} tries:\n\t` + stats
            this.snapshotAfterMsg = afterMsg
            if (estimate.usage > estimate.quota) {
                doOnceWhenBackOnline('makeSpaceAvailable usage exceeds quota', () => {
                    if (!VideoCacheDownloader.exceededQuotaReportedToRollbar) {
                        sendBeacon('makeSpaceAvailable', {
                            afterMsg,
                            estimate,
                            bytesToFree,
                            bytesFreed,
                            initialBytesToFree,
                            totalEstimatedBytesDeleted,
                            requiredToFree,
                            vcrsSafeToDeleteBlobsSize,
                            vcrsSafeToDelete: vcrsSafeToDelete.slice(0, 10),
                            vcrsUnsafeToDeleteWithContent: vcrsUnsafeToDeleteWithContent.slice(0, 10),
                        })
                        VideoCacheDownloader.exceededQuotaReportedToRollbar = true
                    }
                })
            }
            if (vcrsSafeToDelete.length === 0 && getInPortionDownloadMode()) {
                // Typically vcrsSafeToDelete.length === 0
                // means that there's nothing more to delete to free up space and so 
                // makeSpaceAvailable() would return `false` if usage is beyond quota.
                // However, in the context of batch download mode, after we've freed up
                // all we are able to free, we still want to allow
                // downloading more videos, so we return `true` here to
                // allow user to download even beyond their quota.
                // In portion download mode only lasts an hour after the last portion download
                return true
            }
            if (bytesToFree === 0 || bytesFreed >= requiredToFree) {
                if (bytesToFree > 0) {
                    console.warn(`makeSpaceAvailable: bytesToFree (${(bytesToFree/(1024*1024)).toFixed(0)}mb) > 0`)
                }
                console.log(`makeSpaceAvailable successful ${afterMsg}`)
                if (totalEstimatedBytesDeleted !== bytesFreed) {
                    console.warn(`makeSpaceAvailable: bytesFreed (${(bytesFreed / (1024 * 1024)).toFixed(1)}mb)\n\t !== totalEstimatedBytesDeleted (${(totalEstimatedBytesDeleted / (1024 * 1024)).toFixed(1)}mb)`)
                }
                return true
            }

            if (tries >= 1 && this.snapshotNavigatorStorageEstimate?.quota === -1) {
                // on iOS Mobile/Safari navigator.storage.estimate() is not a function so 
                // quota snapshot will be -1
                // Since we cannot know our progress towards a quota, just succeed after we try to free
                // some space
                return true
            }
            if (tries > 10) {
                // if we are not making enough progress, give up
                console.error(`makeSpaceAvailable giving up ${afterMsg}`)
                return false
            }

            requiredToFree = Math.min(oneGb, bytesToFree)
            log(`makeSpaceAvailable attempting to free ${(requiredToFree/(1024*1024)).toFixed(0)}`)

            for (const vcr of vcrsSafeToDelete) {
                const safeToDeleteSize = vcr.size
                if (safeToDeleteSize === 0) continue
                await vcr.deleteBlobs()
                totalEstimatedBytesDeleted += safeToDeleteSize
                if (totalEstimatedBytesDeleted >= requiredToFree) break
            }
            log (`getBytesToFree totalSafeToDeleteSize: ${(totalEstimatedBytesDeleted/(1024*1024)).toFixed(0)}`)

            tries += 1
        }
    }

    static computeSpaceToFree(quota: number, usage: number) {
        return usage <= quota ? 0 : usage - .9 * quota
    }

    async getSafeAndUnsafeVcrsToDelete({ treatRecentAccessDateAsSafe = HOTFIX_UNBLOCK_CACHE_CLEAN_RECENT_BLOBS }: { treatRecentAccessDateAsSafe?: boolean } = {}) {
        const allVcrs = await VideoCacheRecord.getAllVcrsFromCache()
        
        const vcrs = allVcrs.filter(vcr => vcr.getSafeToDeletes({ treatRecentAccessDateAsSafe }).some(b => b))
        const vcrsUnsafeToDelete = allVcrs.filter(vcr => (vcr.uploadeds.length > 0 || vcr.downloadeds.length > 0) && !vcr.getSafeToDeletes({ treatRecentAccessDateAsSafe }).some(b => b))
        let vcrsSecondarySort = vcrs
        if (getHasElectronContext() && vcrs.some(vcr => vcr.accessDate === INHERITED_UPLOAD_ACCESS_DATE)) {
            // in case of INHERITED_UPLOAD_ACCESS_DATE, apply secondary sort
            // based on the latest timestamps in the vcr _id (delete oldest ones first)
            vcrsSecondarySort = sortBy(vcrs, x => extractLastTimestamp(x._id))
        }
        const vcrsSafeToDelete = sortBy(vcrsSecondarySort, x => x.accessDate)
        return { vcrsSafeToDelete, vcrsUnsafeToDelete }
    }

    /**
     * Note, this is an arrow function so that we can pass it like:
     *  VideoCache.videoCacheDownloader.getSafeToDeleteVcrs (mainly for tests)
     * @return {bytesToFree} 0 if we are within quota, otherwise desired bytesToFree
     * @return {estimate} { quota, usage }
     * @return {vcrsSafeToDelete}
     * 1) shared uploads (while connected to local team storage)
     * 2) blobs that haven't been touched within the last hour
     * 3) fully uploaded blobs
     * 4) downloaded blobs
     * @return {vcrsUnsafeToDelete}
     */
    getSafeToDeleteVcrs = async (): Promise<SafeToDeleteVcrsResult> => {
        let estimate = await this.getQuotaAndUsage()
        console.log(`estimate`, { finalEstimate: estimate, storageEstimate: this.snapshotNavigatorStorageEstimate })

        // If we are over quota, free up space equal to 10% of quota
        const firstPassSpaceToFree = VideoCacheDownloader.computeSpaceToFree(estimate.quota, estimate.usage)
        console.log(`firstPassSpaceToFree`, firstPassSpaceToFree)

        if (firstPassSpaceToFree <= 0) return { 
            bytesToFree: firstPassSpaceToFree, vcrsSafeToDelete: [],
            estimate, vcrsUnsafeToDelete: [],
            vcrsUnsafeToDeleteWithContent: [], vcrsSafeToDeleteBlobsSize: -1,
        }
        
        log(`getBytesToFree firstPassSpaceToFree: ${(firstPassSpaceToFree/(1024*1024)).toFixed(0)} > 0`)
        let inPortionDownloadMode = getInPortionDownloadMode()

        const { vcrsSafeToDelete, vcrsUnsafeToDelete } = await this.getSafeAndUnsafeVcrsToDelete({
            /* 
            * `inPortionDownloadMode` is used to determine if we are in the middle of downloading
            * all media in a portion. In this case, protect the videos that are being downloaded
            * from being deleted (i.e. treatRecentAccessDateAsSafe is false)
             */
            treatRecentAccessDateAsSafe: !inPortionDownloadMode,
        })
        // console.log(`### vcrsSafeToDelete`, vcrsSafeToDelete)
        const vcrsUnsafeToDeleteWithContent = vcrsUnsafeToDelete.filter(vcr => vcr.uploadeds.some(b => b) || vcr.downloadeds.some(b => b))
        const vcrsSafeToDeleteBlobsSize = calculateSafeToDeleteBlobsSize(vcrsSafeToDelete)
        this.snapshotSafeToDeleteVcrs = {
            bytesToFree: firstPassSpaceToFree,
            vcrsSafeToDeleteBlobsSize: vcrsSafeToDeleteBlobsSize,
            vcrsUnsafeToDeleteSize: vcrsUnsafeToDeleteWithContent.map(vcr => vcr.size).reduce((a, b) => a + b, 0),
            vcrsUnsafeToDeleteWithContent: vcrsUnsafeToDeleteWithContent.slice(0, 10), // limit to 10 for debugging
        }
        return { 
            bytesToFree: firstPassSpaceToFree, estimate,
            vcrsSafeToDelete, vcrsUnsafeToDelete, vcrsUnsafeToDeleteWithContent,
            vcrsSafeToDeleteBlobsSize,
        }
    }

    /** Get structure with current storage 'usage' and 'quota'.
     * Quota is what the browser is willing to give us or the hard limit of 10G,
     * whichever is less. */
    async getQuotaAndUsage(): Promise<Estimate> {
        // estimate() is not a function on iOS Mobile/Safari
        const { quota, usage } = await navigator?.storage?.estimate?.() ?? { quota: undefined, usage: undefined }
        if (!quota || !usage) {
            warnOnceEstimatorUndefined('navigator.storage.estimate() returned nullish quota or usage', { quota, usage })
        }
        this.snapshotNavigatorStorageEstimate = { quota: quota ?? -1, usage: usage ?? -1 }
        
        // persisted = false means the browser can delete cached data without warning.
        // For a Chrome PWA app persisted should always be true.
        // Safari always returns false.
        // AFAICT this value is only used for reporting and does not affect the behavior of our code.
        this.snapshotNavigatorStoragePersisted = await navigator?.storage?.persisted?.()
        
        const finalEstimate: Estimate = { quota: quota ?? 0, usage: usage ?? 0 }

        if (VideoCacheDownloader.unittestQuota) finalEstimate.quota = VideoCacheDownloader.unittestQuota
        if (VideoCacheDownloader.unittestUsage) finalEstimate.usage = VideoCacheDownloader.unittestUsage

        if (!finalEstimate.usage || !finalEstimate.quota) {
            // iOS Mobile doesn't seem to support navigator.storage.estimate()
            // best we can do is assume we have at least 250mb
            // and that we are at our quota. If we are wrong we'll throw an error
            finalEstimate.quota = 250 * 1024 * 1024 // assume at least 250mb
            // assume we are maxed out on usage and need to free a little space
            finalEstimate.usage = finalEstimate.quota
        }
        /** note videoCacheLimitGB can be set to 2Gb on useMobileLayout otherwise 10 Gb */
        let persistedHardQuota = localStorage.getItem('videoCacheLimitGB') || ''
        let persistedHardQuotaGB = parseFloat(persistedHardQuota) || 10
        let hardQuota = persistedHardQuotaGB * 1024 * 1024 * 1024
        /** TODO? maximize low browser quota (don't take 80%)
         * This requires changes to tests/testing so delay for now.
         * 
         * a browser quota less than hardQuota 10Gb (or 2Gb mobile) represents
         * a low quota imposed by the browser due to it not knowing
         * how important the site is to the user 
         * (e.g. not yet bookmarked or installed as an app or history)
         * In that case use the full quota since space is tight anyhow.
         * 
         * Also consider that a percentage of the usage will be sync docs
         * which are not managed by makeSpaceAvailable(),
         * and so a low sync quota can eventually become filled by this
         * resulting in "Unable to free up space for download"
         * because there may be no vcrs to delete
         */
        let eightyPercentQuota = .8 * finalEstimate.quota
        finalEstimate.quota = Math.min(eightyPercentQuota, hardQuota)
        
        if (didHappenedWithinHourWindow(getDbSaveErrorDetectedAt(), HOURS_TO_DISABLE_IMPLICIT_DOWNLOADS)) {
            const { vcrsSafeToDelete } = await this.getSafeAndUnsafeVcrsToDelete({
                treatRecentAccessDateAsSafe: true,
            })
            if (vcrsSafeToDelete.length > 0) {
                // in the context of a save error, as long as we have vcrs safe to delete, 
                // we should set our quota to match the usage at the time of the
                // save db crash so makeSpaceAvailable() will try to free up space when usage is 
                // over 90% of the quota it's possible that the db save crash happened 
                // when the user tried to download all the portion media
                // which may be well over the hardQuota, but we will try our best not to 
                // remove too many of those videos
                let storageUsageAtCrash = getStorageUsageAtCrash()
                if (storageUsageAtCrash > 0) {
                    finalEstimate.quota = Math.max(storageUsageAtCrash, usage || 0)
                } else {
                    // not quite sure what the usage was at the time of the crash, 
                    // so use current usage (or 200mb) unless quota is less than that
                    finalEstimate.quota = Math.min((usage !== undefined && usage > 0) ? usage : 200 * 1024 * 1024, finalEstimate.quota)
                }
            }
        }
        log(`quota=${(finalEstimate.quota / (1024 * 1024)).toFixed(0)}, usage=${(finalEstimate.usage / (1024 * 1024)).toFixed(0)}`)
        
        this.snapshotFinalEstimate = { ...finalEstimate }
        return finalEstimate
    }

    /**
     * Run stress test on caching mechanism by creating a ton of VideoCacheRecords
     * and associated VideoBlobs.
     * In theory this should be able to run indefinitely since makeSpaceAvailable
     * should keep freeing blobs.
     * Execute from console: window.videoCacheStressTest()
     * Stop by setting VideoCache.stopStressTest = true
     */

    async stressTest() {
        //create blob
        const VCR_BLOBS = 2
        const buffer = new ArrayBuffer(BLOB_BLOCK_SIZE)
        let blob = new Blob([buffer])
        let blobCount = 0

        VideoCacheDownloader.unittestQuota = 4*1024*1024*1024

        while (true) {
            if (VideoCache.stopStressTest) {
                VideoCache.stopStressTest = false
                const { estimate } = await this.getSafeToDeleteVcrs()
                console.log(`Stopping stress test: ${estimate.usage/(1024*1024)}mb used of ${estimate.quota/(1024*1024)}mb quota`)
                return
            }
            let url = '__stress/test/' + String(blobCount).padStart(8, '0')

            blobCount += 1
            if (Math.round(blobCount) % 50 === 0) {
                log(`created ${url}`)
                if (!await this.makeSpaceAvailable()) {
                    console.error('makeSpaceAvailable failed')
                    debugger
                    return
                }
            }

            let vcr = new VideoCacheRecord({ _id: `${url}-${VCR_BLOBS}`})
            if (blobCount % 2 === 0) {
                // for every other video, mark it as uploaded instead of downloaded
                vcr.uploadeds = Array(VCR_BLOBS).fill(true)
            } else {
                vcr.downloadeds = Array(VCR_BLOBS).fill(true)
            }
            vcr.size = BLOB_BLOCK_SIZE * VCR_BLOBS

            try {
                await vcr.saveToDB()
            } catch (error) {
                log(`vcr.saveToDB failed`, error)
                debugger
                return
            }

            // store blobs in cache
            let videoBlob = new VideoBlob(`${url}-1`, blob)
            let videoBlob2 = new VideoBlob(`${url}-2`, blob)
            try {
                await videoBlob.saveToDB()
                await videoBlob2.saveToDB()
            } catch (error) {
                log(`fetchBlob videoBlob.saveToDB failed`, error)
                debugger
                return
            }
        }
    }
}
function calculateSafeToDeleteBlobsSize(vcrsSafeToDelete: VideoCacheRecord[]) {
    return vcrsSafeToDelete.map(vcr => vcr.size).reduce((a, b) => a + b, 0)
}
